import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, Phone, CheckCircle2, Sparkles, Loader2 } from 'lucide-react';
import { axiosInstance } from '@/App';

const STRIPE_SUPPORT_LINKS = {
  support_whatsapp: 'https://buy.stripe.com/3cIbJ2fwzgEp305e9a3F60d',
  support_call: 'https://buy.stripe.com/6oUbJ298b73PgQV1mo3F60e',
};

const Support = () => {
  const [loading, setLoading] = useState({ whatsapp: false, call: false });
  const [supportStatus, setSupportStatus] = useState({ whatsapp_active: false, call_active: false });

  useEffect(() => {
    checkSupportStatus();
  }, []);

  const checkSupportStatus = async () => {
    try {
      const response = await axiosInstance.get('/support/status');
      setSupportStatus(response.data);
    } catch (error) {
      console.error('Error checking support status:', error);
    }
  };

  const handlePurchase = (type) => {
    const subscriptionType = type === 'whatsapp' ? 'support_whatsapp' : 'support_call';
    
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: type === 'whatsapp' ? 'WhatsApp Support' : 'Wöchentlicher Call',
        value: type === 'whatsapp' ? 29.00 : 99.00,
        currency: 'EUR'
      });
    }
    
    setLoading({ ...loading, [type]: true });
    window.location.href = STRIPE_SUPPORT_LINKS[subscriptionType];
  };

  return (
    <div className="space-y-6" data-testid="support-section">
      <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.2)]">
        <CardHeader>
          <CardTitle className="text-2xl">Premium Support Pakete</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[rgba(245,245,247,0.7)] mb-2">
            Benötigen Sie persönliche Unterstützung? Buchen Sie eines unserer Premium Support-Pakete.
          </p>
          <p className="text-sm text-[rgba(245,245,247,0.6)]">
            Perfekt für eine noch erfolgreichere Kampagnen-Optimierung und individuelle Beratung.
          </p>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-8">
        {/* WhatsApp Support */}
        <Card className="shadow-xl border-2 border-[rgba(0,242,255,0.3)] hover:shadow-2xl transition-all hover:scale-105" style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }} data-testid="whatsapp-support-card">
          <CardHeader style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)' }}>
                  <MessageSquare className="w-7 h-7 text-[#00F2FF]" />
                </div>
                <div>
                  <CardTitle className="text-2xl" style={{ color: '#F5F5F7' }}>WhatsApp Support</CardTitle>
                  <p className="text-sm mt-1" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Schnelle Hilfe per Chat</p>
                </div>
              </div>
            </div>
            <div className="text-center mt-6">
              <div className="text-5xl font-bold" style={{ color: '#00F2FF' }}>29€</div>
              <p className="mt-1" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>pro Monat</p>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="bg-[rgba(0,242,255,0.1)] p-5 rounded-lg">
              <h4 className="font-bold mb-3 text-[#F5F5F7]">Leistungen:</h4>
              <ul className="space-y-2 text-sm text-[rgba(245,245,247,0.9)]">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#00F2FF] flex-shrink-0 mt-0.5" />
                  <span><strong>Direkter WhatsApp-Kontakt</strong> mit unserem Experten-Team</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#00F2FF] flex-shrink-0 mt-0.5" />
                  <span><strong>Antworten innerhalb 24 Stunden</strong> (werktags)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#00F2FF] flex-shrink-0 mt-0.5" />
                  <span>Hilfe bei <strong>Kampagnen-Setup</strong> und Ad-Erstellung</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#00F2FF] flex-shrink-0 mt-0.5" />
                  <span><strong>Optimierungs-Tipps</strong> für bessere Performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#00F2FF] flex-shrink-0 mt-0.5" />
                  <span>Unbegrenzte Nachrichten</span>
                </li>
              </ul>
            </div>
            
            {supportStatus.whatsapp_active ? (
              <div className="rounded-lg p-4 text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)', border: '2px solid rgba(0, 242, 255, 0.5)' }}>
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: '#00F2FF' }} />
                <p className="font-bold" style={{ color: '#00F2FF' }}>Aktiv</p>
                <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>WhatsApp Support ist freigeschaltet</p>
              </div>
            ) : (
              <>
                <Button
                  onClick={() => handlePurchase('whatsapp')}
                  disabled={loading.whatsapp}
                  className="w-full h-14 text-lg font-bold"
                  data-testid="purchase-whatsapp-button"
                  style={{
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'
                  }}
                >
                  {loading.whatsapp ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Wird geladen...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Jetzt für 29€/Monat buchen
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-[rgba(245,245,247,0.6)]">Monatlich kündbar</p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Call Support */}
        <Card className="shadow-xl border-4 hover:shadow-2xl transition-all hover:scale-105 relative" style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderColor: 'rgba(167, 139, 250, 0.5)' }} data-testid="call-support-card">
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)' }}>
              <Sparkles className="w-4 h-4" />
              PREMIUM
            </span>
          </div>
          <CardHeader className="pt-8" style={{ backgroundColor: 'rgba(167, 139, 250, 0.08)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(167, 139, 250, 0.15)' }}>
                  <Phone className="w-7 h-7 text-[#A78BFA]" />
                </div>
                <div>
                  <CardTitle className="text-2xl" style={{ color: '#F5F5F7' }}>Wöchentlicher Call</CardTitle>
                  <p className="text-sm mt-1" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>1:1 Video-Beratung</p>
                </div>
              </div>
            </div>
            <div className="text-center mt-6">
              <div className="text-5xl font-bold" style={{ color: '#A78BFA' }}>99€</div>
              <p className="mt-1" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>pro Monat</p>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="bg-[rgba(167,139,250,0.1)] p-5 rounded-lg">
              <h4 className="font-bold mb-3 text-[#F5F5F7]">Premium Leistungen:</h4>
              <ul className="space-y-2 text-sm text-[rgba(245,245,247,0.9)]">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#A78BFA] flex-shrink-0 mt-0.5" />
                  <span><strong>1 Stunde Video-Call pro Woche</strong> (4x pro Monat)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#A78BFA] flex-shrink-0 mt-0.5" />
                  <span><strong>Individuelle Kampagnen-Analyse</strong> & Strategieplanung</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#A78BFA] flex-shrink-0 mt-0.5" />
                  <span><strong>Live Screen-Sharing</strong> für direkte Optimierung</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#A78BFA] flex-shrink-0 mt-0.5" />
                  <span>Zugang zur <strong>WhatsApp Support-Gruppe</strong> inklusive</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#A78BFA] flex-shrink-0 mt-0.5" />
                  <span><strong>Prioritäts-Support</strong> bei allen Anfragen</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#A78BFA] flex-shrink-0 mt-0.5" />
                  <span>Exklusive <strong>Marketing-Insights</strong> & Best Practices</span>
                </li>
              </ul>
            </div>
            
            {supportStatus.call_active ? (
              <div className="rounded-lg p-4 text-center" style={{ backgroundColor: 'rgba(167, 139, 250, 0.15)', border: '2px solid rgba(167, 139, 250, 0.5)' }}>
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: '#A78BFA' }} />
                <p className="font-bold" style={{ color: '#A78BFA' }}>Aktiv</p>
                <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Wöchentlicher Call ist freigeschaltet</p>
              </div>
            ) : (
              <>
                <Button
                  onClick={() => handlePurchase('call')}
                  disabled={loading.call}
                  className="w-full h-14 text-lg font-bold"
                  data-testid="purchase-call-button"
                  style={{
                    background: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
                    color: '#0B0E14'
                  }}
                >
                  {loading.call ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Wird geladen...
                    </>
                  ) : (
                    <>
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt für 99€/Monat buchen
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-[rgba(245,245,247,0.6)]">Monatlich kündbar</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Note */}
      <Card className="shadow-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(0, 242, 255, 0.2)' }} data-testid="support-info">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div>
              <h4 className="font-semibold mb-2" style={{ color: '#F5F5F7' }}>Individueller Support für maximalen Erfolg</h4>
              <p className="text-sm mb-3" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                Beide Support-Pakete sind perfekt für Finanzdienstleister, die ihre Meta-Kampagnen auf das nächste Level heben möchten. 
                Profitieren Sie von unserer jahrelangen Erfahrung in der Finanzbranche.
              </p>
              <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                <strong>Hinweis:</strong> Die Abos sind monatlich kündbar. Nach erfolgreicher Buchung erhalten Sie Zugang zu den jeweiligen Support-Kanälen.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Support;
